// @flow strict

import {
  Button,
  Card,
  PaidButton,
  type PropsWithChildren,
  TrackEvents,
  URLS,
} from '@mondough/public-web-components'
import { BespokeCardContent } from '@mondough/public-web-components/src/molecules/Card/Content'
import { prettyPrintPriceFromMinor } from '@mondough/public-web-components/src/utils/currencyUtils'
import styles from './PlusBundle.module.scss'
import SubtitleSection from '../../SubtitleSection'
import PaidSectionWrapper from '../../PaidSectionWrapper'

import cx from 'classnames'
import * as React from 'react'
import { useEffect } from 'react'
import coreCard from '../../../../static/images/cards/core-hot-coral.png'
import holoCard from '../../../../static/images/cards/holo-blue.png'
import metalCard from '../../../../static/images/cards/metal-white.png'
// Images
import Placeholder from '../../../../static/images/icons/placeholder.png'
import ModalWrapper from '../../ModalWrapper'
import PaidBundlesComparisonTable from './ComparisonTable'
import { featuresToObject, featuresToTable } from './PlusBundles.utils'

const cardAssets = {
  metal: metalCard,
  holo: holoCard,
  core: coreCard,
}

export type Feature = {|
  id?: string,
  title?: string,
  description?: string,
  icon?: string,
  value?: string,
|}

export type Bundle = {|
  id?: 'core' | 'holo' | 'metal',
  title?: string,
  monthlyAmountInCents?: number,
  currency?: string,
  minTermInMonths?: number,
  features?: string[],
  cta?: {
    text?: string,
    href?: string,
    gaCategory?: string,
    gaAction?: string,
    gaLabel?: string,
  },
|}

const PlusBundles = ({
  features,
  bundles,
  icons,
  children,
  table,
  showPremium,
}: PropsWithChildren<
  {|
    bundles: Bundle[],
    features?: Feature[],
    icons?: { [k: string]: string },
    table?: { [k: 'core' | 'holo' | 'metal']: string[] },
    showPremium?: boolean,
  |},
  React.Element<typeof SubtitleSection>,
>) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const scroller = React.useRef()

  // This makes sure that Premium is always shown first (ie scrolled right all the way)
  useEffect(() => {
    if (scroller && scroller.current) {
      scroller.current.scrollLeft += 3000
    }
  }, [])

  // This way flow knows for sure these aren't undef the rest of the time
  // It also helps us hide the whole section if any of the data is bad
  if (features == null) return null
  if (bundles == null) return null
  if (table == null) return null

  const featuresMap = featuresToObject(features, icons)
  const featuresTable = featuresToTable(featuresMap, table)

  // Make the prices shape real nice for the comparison table
  const tablePricing = bundles.reduce(
    (
      bundlesPricingAsObj: {
        core?: number,
        holo?: number,
        metal?: number,
        currency?: string,
      },
      bundle: Bundle,
    ) => {
      if (bundle.id != null && bundle.monthlyAmountInCents != null) {
        bundlesPricingAsObj[bundle.id] = bundle.monthlyAmountInCents
      }
      return bundlesPricingAsObj
    },
    // The currency is constant across all bundles so we initialise the resulting object with that information already populated. We'll then add an entry for each price in the array.
    { currency: bundles[0].currency },
  )

  return (
    <PaidSectionWrapper
      contentClassName={styles.content}
      className={styles.section}
      id="bundles"
    >
      <div className={styles.subtitleSectionWrapper}>{children}</div>
      <div className={styles.contentInner} ref={scroller}>
        <div className={styles.overflowContainer}>
          <div className={styles.cards}>
            {bundles.map((bundle, i) => {
              if (bundle == null || bundle.title == null) return null
              const {
                id,
                title,
                monthlyAmountInCents,
                minTermInMonths,
                features,
                cta,
                currency,
              } = bundle || {}
              return (
                <div className={cx(styles.cardContainer)} key={id}>
                  <Card as={<div />} className={styles.card}>
                    <Card.Media>
                      <BespokeCardContent>
                        <div
                          className={cx(
                            styles.cardTop,
                            styles[`bundle${i}background`],
                          )}
                        >
                          <div className={styles.cardTopTextSection}>
                            <h3 className={styles.cardTitleFont}>{title}</h3>
                            <p className={styles.cardSubtitle}>
                              {monthlyAmountInCents &&
                              monthlyAmountInCents > 0 ? (
                                <>
                                  {currency === 'GBP' && <span>£</span>}
                                  <span className={''}>
                                    {prettyPrintPriceFromMinor(
                                      monthlyAmountInCents,
                                    )}
                                  </span>{' '}
                                  per month
                                </>
                              ) : (
                                <span className={''}>Free</span>
                              )}
                            </p>
                          </div>
                          <div className={styles.cardTopImgSection}>
                            {bundle && bundle.id && (
                              <img
                                alt=""
                                src={cardAssets[bundle.id]}
                                className={styles.cardAsset}
                              />
                            )}
                          </div>
                        </div>
                      </BespokeCardContent>
                    </Card.Media>
                    <Card.Content className={styles.cardContent}>
                      <BespokeCardContent>
                        <BespokePlusBundleCardContent
                          features={features}
                          minTerm={minTermInMonths}
                          featuresMap={featuresMap}
                          price={monthlyAmountInCents}
                          title={title}
                          cta={cta}
                          bundleIndex={i}
                          setIsModalOpen={setIsModalOpen}
                        />
                      </BespokeCardContent>
                    </Card.Content>
                  </Card>
                </div>
              )
            })}
          </div>
        </div>
        <div className={styles.modalCTASSection}>
          <h3>Not sure what to pick?</h3>
          <p>Compare each account to find the right one for you</p>
          <div className={styles.modalCTASSectionButton}>
            <PaidButton onClick={() => setIsModalOpen(true)}>
              Compare Accounts
            </PaidButton>
          </div>
        </div>
      </div>

      <ModalWrapper
        isOpen={isModalOpen}
        onDismiss={() => setIsModalOpen(false)}
        ariaLabelledby="modalHeader"
      >
        <h1 id={`modalHeader`} className={styles.modalTitle}>
          Compare all features
        </h1>
        <p className={styles.modalHeadline}>
          Every feature, listed. Find the right account for you.
        </p>
        <hr className={styles.modalHr} />
        {featuresTable != null && (
          <PaidBundlesComparisonTable
            featuresTable={featuresTable}
            pricing={tablePricing}
            showPremium={showPremium}
          />
        )}
      </ModalWrapper>
    </PaidSectionWrapper>
  )
}

type FeatureListItemProps = {|
  id: string,
  iconSrc?: string,
  title: string,
  description?: string,
  backgroundStyles?: string,
|}

const FeatureListItem = ({
  id,
  iconSrc,
  title,
  description,
  backgroundStyles,
  children,
}: PropsWithChildren<FeatureListItemProps>) => (
  <li className={cx(styles.feature, backgroundStyles)} key={id}>
    <img src={iconSrc} alt="" width="36" className={styles.featureIcon} />
    <div className={styles.featureTextArea}>
      <p className={styles.featureName}>{title}</p>
      {description && <p className={styles.featureCaption}>{description}</p>}
    </div>
    {children}
  </li>
)

const BespokePlusBundleCardContent = ({
  features,
  minTerm,
  featuresMap,
  price,
  title,
  cta,
  bundleIndex,
  setIsModalOpen,
}) => {
  return (
    <div className={styles.cardTextSection}>
      <ul>
        {bundleIndex > 0 && (
          <FeatureListItem
            title={'All free Monzo features'}
            id={'all-free-monzo'}
            iconSrc={Placeholder}
            backgroundStyles={styles.bundle0background}
          >
            <Button
              className={styles.showMoreBtn}
              onClick={() => setIsModalOpen(true)}
            >
              Show
            </Button>
          </FeatureListItem>
        )}
        {bundleIndex > 1 && (
          <FeatureListItem
            title={'All Monzo plus features'}
            id={'all-plus-monzo'}
            iconSrc={Placeholder}
            backgroundStyles={styles.bundle1background}
          >
            <Button
              className={styles.showMoreBtn}
              onClick={() => setIsModalOpen(true)}
            >
              Show
            </Button>
          </FeatureListItem>
        )}
        {features && Array.isArray(features) && features.length
          ? features.map((featureID: string, i) => {
              // No good data no UI, those are the rules
              if (
                featuresMap == null ||
                featuresMap[featureID] == null ||
                featuresMap[featureID].title == null ||
                featuresMap[featureID].description == null
              )
                return null

              const { title, description, icon } = featuresMap[featureID]
              return (
                <FeatureListItem
                  title={title}
                  description={description}
                  id={featureID}
                  iconSrc={icon}
                  key={featureID}
                />
              )
            })
          : null}
      </ul>
      <div className={styles.cardFooter}>
        {minTerm != null &&
          (minTerm > 0 ? (
            <p>{minTerm} month minimum</p>
          ) : (
            <p>No minimum term</p>
          ))}
        {cta && (cta.text || title) && (
          <div className={styles.cardFooterCTA}>
            <TrackEvents
              category={cta.gaCategory || 'conversion'}
              action={cta.gaAction || 'download app'}
              label={cta.gaLabel || 'download'}
            >
              <PaidButton href={cta.href || URLS.ADJUST_BOTH_STORE}>
                {cta.text || (title != null ? `Get ${title}` : '')}
              </PaidButton>
            </TrackEvents>
          </div>
        )}
      </div>
    </div>
  )
}

export default PlusBundles
